import { useCallback } from 'react';
import { uniqueId } from 'lodash';
import { useDebouncedCallback } from 'use-debounce';

import { insertFileTextToPrompt, PROMPT_MENU_TYPE } from 'src/components/RichSlate';
import { EXTERNAL_CHANNEL } from 'src/data/channel';
import {
  getResourceDocumentErrorMessage,
  getResourceImageErrorMessage,
  RESOURCE_CONTENT_TYPE,
  RESOURCE_CONTENT_TYPE_DIRECT_UPLOAD,
  RESOURCE_FILE_UPLOAD_ERROR_CODE,
} from 'src/data/performance';

import { createNewUrlItem, filterBrandBriefItems, filterUrlItems } from '../../utils';

const TIMEOUT = 1000;

export const useHandlers = ({
  openAddResourcePopup,
  addPromptImage,
  addPromptDocument,
  removeUploadingImage,
  removeUploadingDocument,
  setImageError,
  setDocumentError,
  setImageUploadSuccess,
  setDocumentUploadSuccess,
  imageResources,
  documentResources,
  imageItems,
  documentItems,
  setMenuType,
  onCloseMenu,
  isUploadingImage,
  isUploadingDocument,
  index,
  isHtmlEmpty,
  setSearchValue,
  urlResources,
  setFilteredUrlItems,
  brandBriefsMenuItems,
  setFilteredBankItems,
  onUrlClick,
  onImageClick,
  onDocumentClick,
  noTags,
  setUploadingDocumentName,
  clearImageError,
  clearDocumentError,
  editor,
  setIsUploadingImage,
  setIsUploadingDocument,
  resourcesTypeFilter,
  resourcesTagFilter,
  setResourcesTagFilter,
  setResourcesTypeFilter,
  trackingEvents,
  searchValue,
  onConnectExternalChannel,
}) => {
  const handleItemClick = useCallback(
    async (type) => {
      onCloseMenu();

      const randomId =
        (type === PROMPT_MENU_TYPE.image ? 'uploadImage-' : 'uploadDocument-') + uniqueId();
      const uploadedFile = await openAddResourcePopup({
        type:
          type === PROMPT_MENU_TYPE.image
            ? RESOURCE_CONTENT_TYPE_DIRECT_UPLOAD.img
            : RESOURCE_CONTENT_TYPE_DIRECT_UPLOAD.document,
        onHiddenError: (error, id) => {
          if (id) {
            const resources = type === PROMPT_MENU_TYPE.image ? imageResources : documentResources;
            const file = resources.find((resource) => resource.id === id);
            if (file) {
              switch (type) {
                case PROMPT_MENU_TYPE.image:
                  addPromptImage(file, randomId, index);
                  insertFileTextToPrompt(editor, isHtmlEmpty, file, RESOURCE_CONTENT_TYPE.image);
                  break;
                case PROMPT_MENU_TYPE.document:
                  addPromptDocument(file, randomId, index);
                  insertFileTextToPrompt(editor, isHtmlEmpty, file, RESOURCE_CONTENT_TYPE.document);
                  break;
                default:
                  break;
              }
            } else {
              switch (type) {
                case PROMPT_MENU_TYPE.document:
                  setDocumentError(
                    id,
                    getResourceDocumentErrorMessage(
                      RESOURCE_FILE_UPLOAD_ERROR_CODE.fileExistsButNotAssociatedToWorkspace
                    )
                  );
                  break;
                case PROMPT_MENU_TYPE.image:
                  setImageError(
                    id,
                    getResourceImageErrorMessage(
                      RESOURCE_FILE_UPLOAD_ERROR_CODE.fileExistsButNotAssociatedToWorkspace
                    )
                  );
                  break;
                default:
                  break;
              }
            }
            switch (type) {
              case PROMPT_MENU_TYPE.image:
                setIsUploadingImage(false);
                break;
              case PROMPT_MENU_TYPE.document:
                setUploadingDocumentName(null);
                setIsUploadingDocument(false);
                break;
              default:
                break;
            }

            return;
          }
          switch (type) {
            case PROMPT_MENU_TYPE.image:
              if (!isUploadingImage) {
                setImageError(id, error);
                removeUploadingImage(randomId);
                setIsUploadingImage(false);
              }
              break;
            case PROMPT_MENU_TYPE.document:
              if (!isUploadingDocument) {
                setDocumentError(id, error);
                removeUploadingDocument(randomId);
                setUploadingDocumentName(null);
                setIsUploadingDocument(false);
              }
              break;
            default:
              break;
          }
        },
        onStartUpload: (name) => {
          switch (type) {
            case PROMPT_MENU_TYPE.image:
              setIsUploadingImage(true, randomId, index);
              break;
            case PROMPT_MENU_TYPE.document:
              setUploadingDocumentName(name);
              setIsUploadingDocument(true, name, randomId, index);
              break;
            default:
              break;
          }
        },
        onStopLoading: () => {
          switch (type) {
            case PROMPT_MENU_TYPE.image:
              removeUploadingDocument(randomId);
              setIsUploadingImage(false);
              break;
            case PROMPT_MENU_TYPE.document:
              setUploadingDocumentName(null);
              removeUploadingDocument(randomId);
              setIsUploadingDocument(false);
              break;
            default:
              break;
          }
        },
      });
      if (uploadedFile?.status === 'active') {
        switch (type) {
          case PROMPT_MENU_TYPE.image:
            clearImageError();
            onImageClick(uploadedFile, false, randomId);
            setImageUploadSuccess(uploadedFile);
            break;
          case PROMPT_MENU_TYPE.document:
            clearDocumentError();
            onDocumentClick(uploadedFile, false, randomId);
            setDocumentUploadSuccess(uploadedFile);
            break;
          default:
            break;
        }
      }
      setIsUploadingImage(false);
    },
    [
      onCloseMenu,
      openAddResourcePopup,
      imageResources,
      addPromptImage,
      setIsUploadingImage,
      isUploadingImage,
      setImageError,
      clearImageError,
      setImageUploadSuccess,
      onImageClick,
      clearDocumentError,
      onDocumentClick,
      isUploadingDocument,
      setIsUploadingDocument,
      setDocumentError,
      setDocumentUploadSuccess,
      addPromptDocument,
      documentResources,
      setUploadingDocumentName,
      index,
      editor,
      isHtmlEmpty,
      removeUploadingDocument,
      removeUploadingImage,
    ]
  );

  const debouncedTrackResourceSearchSubmitted = useDebouncedCallback(
    ({ term, tagsFilters, typeFilters }) => {
      trackingEvents?.trackResourceSearchSubmitted({
        term,
        tagsFilters: tagsFilters.join(', '),
        typeFilters: typeFilters.join(', '),
      });
    },
    TIMEOUT
  );

  const handleSearchChange = useCallback(
    (value, type) => {
      if (type === PROMPT_MENU_TYPE.url) {
        setSearchValue(value.trim());

        const items = filterUrlItems(value, urlResources);

        if (items.length === 0) {
          const newItem = createNewUrlItem(value, onUrlClick, noTags);
          if (newItem) {
            items.push(newItem);
          }
        }
        setFilteredUrlItems([...items]);
      } else {
        setSearchValue(value);

        debouncedTrackResourceSearchSubmitted({
          term: value,
          tagsFilters: resourcesTagFilter,
          typeFilters: resourcesTypeFilter,
        });

        const items = filterBrandBriefItems(value, brandBriefsMenuItems);
        setFilteredBankItems(items);
      }
    },
    [
      urlResources,
      setSearchValue,
      onUrlClick,
      noTags,
      brandBriefsMenuItems,
      setFilteredUrlItems,
      setFilteredBankItems,
      debouncedTrackResourceSearchSubmitted,
      resourcesTagFilter,
      resourcesTypeFilter,
    ]
  );

  const handleMenuCardClick = useCallback(
    (event) => {
      const type = event.currentTarget?.dataset?.type;
      if (!type) {
        return;
      }

      if (type === PROMPT_MENU_TYPE.salesforce) {
        onConnectExternalChannel({
          channel: EXTERNAL_CHANNEL.salesforceCrm,
          callback: () => setMenuType(type),
        });
        return;
      }

      setMenuType(type);

      // If we don't have existing images or documents - execute "upload" flow right away
      if (
        (type === PROMPT_MENU_TYPE.image && imageItems?.length === 0 && !isUploadingImage) ||
        (type === PROMPT_MENU_TYPE.document && documentItems?.length === 0 && !isUploadingDocument)
      ) {
        handleItemClick(type);
      }
    },
    [
      imageItems?.length,
      documentItems?.length,
      isUploadingImage,
      handleItemClick,
      setMenuType,
      isUploadingDocument,
      onConnectExternalChannel,
    ]
  );

  const handleChangeResourcesTagFilter = useCallback(
    (newValue) => {
      setResourcesTagFilter(newValue);
      trackingEvents?.trackResourceSearchSubmitted({
        term: searchValue,
        tagsFilters: newValue.join(', '),
        typeFilters: resourcesTypeFilter.join(', '),
      });
    },
    [setResourcesTagFilter, trackingEvents, searchValue, resourcesTypeFilter]
  );

  const handleChangeResourcesTypeFilter = useCallback(
    (newValue) => {
      setResourcesTypeFilter(newValue);
      trackingEvents?.trackResourceSearchSubmitted({
        term: searchValue,
        tagsFilters: resourcesTagFilter.join(', '),
        typeFilters: newValue.join(', '),
      });
    },
    [setResourcesTypeFilter, trackingEvents, searchValue, resourcesTagFilter]
  );

  return {
    handleItemClick,
    handleSearchChange,
    handleMenuCardClick,
    handleChangeResourcesTagFilter,
    handleChangeResourcesTypeFilter,
  };
};
